// Consolidated summary tab. Renders the same 8 sections as the Excel
// "Consolidated Summary" sheet, refreshed from /:id/summary whenever
// items change. Read-only: edits happen in the Items tab.
import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { formatNumber } from "../../../lib/utils";

interface Row {
  size: string;
  material: string;
  description: string;
  schedule?: string;
  total: number;
  unit: string;
}

interface Sections {
  pipeMetres: Row[];
  flanges: Row[];
  fittings: Row[];
  pipeCuts: Row[];
  rollGrooves: Row[];
  airBottles: Row[];
  studsDrains: Row[];
  welds: Row[];
}

const SECTIONS: Array<{ key: keyof Sections; label: string; schedule?: boolean }> = [
  { key: "pipeMetres", label: "1. Pipe — Lineal metres", schedule: true },
  { key: "flanges", label: "2. Flanges" },
  { key: "fittings", label: "3. Fittings" },
  { key: "pipeCuts", label: "4. Pipe cuts", schedule: true },
  { key: "rollGrooves", label: "5. Roll grooves", schedule: true },
  { key: "airBottles", label: "6. Air bottles" },
  { key: "studsDrains", label: "7. Studs / drains / taps" },
  { key: "welds", label: "8. Welds" },
];

interface Props {
  projectId: number;
  reloadKey: number;
}

export function ConsolidatedSummary({ projectId, reloadKey }: Props) {
  const [sections, setSections] = useState<Sections | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    void (async () => {
      try {
        const r = await api<{ sections: Sections }>(
          `/api/tools/pipe-takeoff/projects/${projectId}/summary`,
        );
        setSections(r.sections);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [projectId, reloadKey]);

  if (error) {
    return (
      <div className="bsp-card" style={{ borderColor: "var(--bsp-red)" }}>
        <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
      </div>
    );
  }

  if (!sections) return <p>Loading summary…</p>;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {SECTIONS.map(({ key, label, schedule }) => {
        const rows = sections[key];
        const sectionTotal = rows.reduce((sum, r) => sum + r.total, 0);
        return (
          <div key={key} className="bsp-card" style={{ padding: 0 }}>
            <h3 style={{ margin: 0, padding: "10px 14px", background: "var(--bsp-blue)", color: "var(--ink-000)", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {label}
            </h3>
            {rows.length === 0 ? (
              <p style={{ padding: 14, margin: 0, color: "var(--bsp-fg-muted)", fontStyle: "italic" }}>
                No items
              </p>
            ) : (
              <table className="bsp-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Size</th>
                    <th>Material</th>
                    {schedule && <th>Schedule</th>}
                    <th style={{ textAlign: "right" }}>Total</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.description}</td>
                      <td className="bsp-mono">{r.size}</td>
                      <td>{r.material}</td>
                      {schedule && <td className="bsp-mono">{r.schedule ?? ""}</td>}
                      <td className="bsp-mono" style={{ textAlign: "right" }}>
                        {formatNumber(r.total, r.unit === "m" ? 2 : 0)}
                      </td>
                      <td>{r.unit}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={schedule ? 4 : 3} style={{ fontWeight: 700 }}>
                      Section total
                    </td>
                    <td className="bsp-mono" style={{ textAlign: "right", fontWeight: 700 }}>
                      {formatNumber(sectionTotal, 2)}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}
